<template>

    <input @click="open" @keyup="open" @change="open" :value="display" required type="text" class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md">

    <div class="relative z-10" role="dialog" aria-modal="true">

        <transition
            enter-active-class="ease-out duration-300"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="ease-in duration-200"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div v-if="isOpen" @click="isOpen = false" class="fixed inset-0 bg-gray-500 bg-opacity-25 transition-opacity"></div>
        </transition>

        <transition
            enter-active-class="ease-out duration-300"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="ease-in duration-200"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
        >
            <div v-if="isOpen" @click.self="isOpen = false" class="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
                <div class="mx-auto max-w-3xl transform divide-y divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
                    <div class="relative">
                        <!-- Heroicon name: solid/search -->
                        <svg class="pointer-events-none absolute top-3.5 left-4 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
                        </svg>
                        <input v-model="query" @keyup="updateSuggestionsDebounced" type="text" name="query" id="query" ref="query" class="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-800 placeholder-gray-400 focus:ring-0 sm:text-sm" placeholder="Vyhledávání..." role="combobox" aria-expanded="false" aria-controls="options">
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-2 flex divide-x divide-gray-100">
                        <!-- Preview Visible: "sm:h-96" -->
                        <div v-if="results.length > 0" class="max-h-96 min-w-0 flex-auto scroll-py-4 overflow-y-auto px-6 py-4 sm:h-96">
                            <!-- todo this sometimes blinks when not supposed to (deleting query with results) -->
                            <h2 v-if="!query && !debounce" class="mt-2 mb-4 text-xs font-semibold text-gray-500">Nedávné kontakty</h2>

                            <ul v-if="results.length > 0" class="-mx-2 text-sm text-gray-700" id="options" role="listbox">
                                <!-- Active: "bg-gray-100 text-gray-900" -->
                                <li @mouseenter="openResult = index" v-for="(result, index) in results" :class="openResult === index ? 'bg-gray-100 text-gray-900' : ''" class="group flex cursor-default select-none items-center rounded-md p-2" id="option-1" role="option" tabindex="-1">
                                    <img :src="'/img/' + getTypeForIcon(result.type) + '_type_icon.png'" alt="" class="h-6 w-6 flex-none rounded-full">
                                    <span class="ml-3 flex-auto truncate">{{ result.name }}</span>
                                    <!-- Not Active: "hidden" -->
                                    <!-- Heroicon name: solid/chevron-right -->
                                    <svg v-if="openResult !== index ? '' : 'hidden'" class="ml-3 h-5 w-5 flex-none text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                                    </svg>
                                </li>
                            </ul>
                        </div>

                        <div v-if="results.length > 0" class="h-96 flex-none flex-col divide-y divide-gray-100 overflow-y-auto sm:flex">
                            <div class="flex-none p-6 text-center">
                                <img :src="'/img/' + getTypeForIcon(results[openResult].type)  + '_type_icon.png'" alt="" class="mx-auto h-16 w-16 rounded-full">
                                <h2 class="mt-3 font-semibold text-gray-900">{{ results[openResult].name }}</h2>
                                <p class="text-sm leading-6 text-gray-500"> {{ results[openResult].address }} </p>
                            </div>
                            <div class="flex flex-auto flex-col justify-between p-6">
                                <dl class="grid grid-cols-1 gap-x-6 gap-y-3 text-sm text-gray-700">
                                    <dt class="col-end-1 font-semibold text-gray-900">Název</dt>
                                    <dd> {{results[openResult].name}} </dd>
                                    <dt class="col-end-1 font-semibold text-gray-900">Adresa</dt>
                                    <dd class="truncate"> {{ results[openResult].address }} </dd>
                                    <dt class="col-end-1 font-semibold text-gray-900">Typ</dt>
                                    <dd class="truncate"> {{ results[openResult].type }} </dd>
                                </dl>
                                <button @click="select" type="button" class="mt-6 w-full rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                                    Vybrat
                                </button>
                            </div>
                        </div>
                    </div>

                    <div v-if="results.length === 0" class="py-14 px-6 text-center text-sm sm:px-14">
                        <Spinner class="h-8 w-8" :color="'after:bg-indigo-600'" v-if="working"/>
                        <div v-else>
                            <svg class="mx-auto h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                            <p class="mt-4 font-semibold text-gray-900">Žádné výsledky</p>
                            <p class="mt-2 text-gray-500">Pod hledaným výrazem jsme bohužel nic nenašli.</p>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

    </div>
</template>

<script>
import Spinner from "./Spinner";
export default {
    name: "AddressSearch",
    components: {Spinner},
    props: ['isOpen', 'display'],
    emits: ['selected', 'closed'],
    data() {
        return {
            query: '',
            isOpen: false,
            openResult: 0,
            results: [],

            working: false,

            debounce: null,
        }
    },
    mounted(){
        this.getContacts();
    },
    methods: {
        getTypeForIcon(type) {
            if(type.includes('_')){
                return type.split('_')[0];
            } else return type;
        },
        getContacts(){
            axios.get(route('fapi.contacts'))
                .then( (response) => {
                    console.log(response.data);
                    if(this.results.length === 0)
                        this.results = response.data.contacts;
                });
        },
        select(){
            let result = {
                id: this.results[this.openResult].id,
                type: this.results[this.openResult].type,
                name: this.results[this.openResult].name + ', ' + this.results[this.openResult].address + ' (' + this.results[this.openResult].id + ')',
            }
            this.$emit('selected', result);
            this.isOpen = false;
        },
        open(){
            this.isOpen = true;
            setTimeout(this.setFocus, 50);
        },
        setFocus(){
            this.$refs.query.focus()
        },
        updateSuggestionsDebounced(){
            if(this.debounce) {
                clearTimeout(this.debounce);
                this.debounce = null;
            }
            this.debounce = setTimeout(this.updateSuggestions, 200);
        },
        updateSuggestions(){
            if(this.debounce) this.debounce = null;
            this.working = true;

            let query = this.query ?? '';
            console.log(query)
            if(query === '') {
                this.results = [];
                this.working = false;
                return;
            }

            if(query.length < 3) return;
            if(this.typeSelected == null)
                return axios.get(route('fapi.searchbox', {query: query}))
                    .then((response) => {
                        this.results = response.data ?? [];
                        this.openResult = 0;
                        this.working = false;
                    });
            else
                return axios.get(route('fapi.searchbox', {query: query, type: this.typeSelected}))
                    .then((response) => {
                        this.results = response.data ?? [];
                        this.openResult = 0;
                        this.working = false;
                    });
        },

    }
}
</script>

<style scoped>

</style>
